// ─── phone.js ───────────────────────────────────────────────────────────────
// Rules for phone numbers. The phone number is the account's ID, so it must be
// right. This app is for Singapore homes, so we only accept Singapore mobile
// numbers: 8 digits, starting with 8 or 9.
//
// The backend's PhoneNumbers.java follows exactly the same rules.
//
// What we save is always the 8 digits on their own, e.g. "91234567".
//
//   "9123 4567"      → valid      → "91234567"
//   "+65 9123 4567"  → valid      → "91234567"
//   "6591234567"     → valid      → "91234567"
//   "9123 4567abc"   → NOT valid  (letters)
//   "912345678"      → NOT valid  (9 digits)
//   "61234567"       → NOT valid  (doesn't start with 8 or 9)

export const PHONE_LENGTH = 8;

// The only characters allowed in what the person typed: digits, spaces
// (including the "non-breaking" space some apps add when you copy a number),
// +, -, brackets and dots.
const ALLOWED_CHARACTERS = /^[0-9 \t\u00A0+\-().]*$/;

// A Singapore mobile number: 8 or 9, then 7 more digits.
const SG_MOBILE = /^[89][0-9]{7}$/;

// Keeps only the digits, and drops a leading "65" country code.
// "+65 9123 4567" → "91234567"
export function normalisePhone(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('65')) return digits.slice(2);
  return digits;
}

// Checks a phone number. Returns '' if it's fine, or a message to show the
// person if it isn't.
export function phoneError(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') {
    return 'Please enter your phone number.';
  }
  if (!ALLOWED_CHARACTERS.test(text)) {
    return 'Phone numbers can only contain digits.';
  }
  const phone = normalisePhone(text);
  if (phone.length !== PHONE_LENGTH) {
    return 'Please enter an 8-digit Singapore mobile number, for example 9123 4567.';
  }
  if (!SG_MOBILE.test(phone)) {
    return 'Singapore mobile numbers start with 8 or 9.';
  }
  return '';
}

export function isValidPhone(raw) {
  return phoneError(raw) === '';
}

// Used by the phone input box on every keystroke and paste.
// Turns what's in the box into digits only (letters are simply dropped).
// If someone pastes "+65 9123 4567", the "65" is removed.
// Returns null when there would be more than 8 digits: the box then ignores
// that change, so a 9th digit can't be typed and a wrong number can't be
// pasted in and quietly cut short.
export function cleanPhoneInput(text) {
  let digits = String(text ?? '').replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('65')) digits = digits.slice(2);
  if (digits.length > PHONE_LENGTH) return null;
  return digits;
}

// Adds a space after the 4th digit so the number is easy to read and check.
// "91234567" → "9123 4567"
export function formatPhone(digits) {
  const d = String(digits ?? '');
  return d.length > 4 ? `${d.slice(0, 4)} ${d.slice(4)}` : d;
}